<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="1" />

    <div class="container mt-3">
      <div class="card p-3">
        <h5 class="mb-3">Evaluación Antropométrica (IMC)</h5>

        <!-- Datos personales -->
        <div class="row mb-3">
          <div class="col-4">
            <label class="form-label">Edad (años)</label>
            <input type="number" class="form-control" v-model.number="edad" min="1" />
          </div>

          <div class="col-4">
            <label class="form-label">Sexo</label>
            <select class="form-select" v-model="sexo">
              <option value="">Seleccione</option>
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
            </select>
          </div>

          <div class="col-4 d-flex align-items-end">
            <span class="badge bg-secondary">
              {{ esAdulto ? 'Adulto' : 'Niño / Adolescente' }}
            </span>
          </div>
        </div>

        <!-- Peso / Talla -->
        <div class="row mb-3">
          <div class="col-6">
            <label class="form-label">Peso (kg)</label>
            <input type="number" class="form-control" v-model.number="peso" />
          </div>

          <div class="col-6">
            <label class="form-label">Talla (cm)</label>
            <input type="number" class="form-control" v-model.number="talla" />
          </div>
        </div>

        <button class="btn btn-primary" @click="calcular">
          Calcular
        </button>

        <!-- Resultado -->
        <div v-if="imc" class="mt-4">
          <h6>Resultado</h6>

          <p><strong>IMC:</strong> {{ imc }}</p>

          <span class="badge" :class="badgeClase">
            {{ clasificacion }}
          </span>

          <p class="mt-2"><strong>Peso ideal estimado:</strong> {{ pesoIdeal }} kg</p>
          <p><strong>Superficie corporal (BSA):</strong> {{ superficieCorporal }} m²</p>

          <p class="mt-2 text-muted">
            {{ recomendacion }}
          </p>

          <p class="text-muted">
            Calculado el {{ fechaCalculo }}
          </p>
        </div>
      </div>
<div class="text-end mt-3">
  <button
    v-if="imc"
    class="btn btn-outline-danger"
    @click="exportarPDF"
  >
    Exportar PDF
  </button>
</div>

    </div>

    <Footer />
  </div>
</template>
<script>
import Layout from '@/components/Layout.vue'
import Footer from '@/components/Footer.vue'
import Swal from 'sweetalert2'
import jsPDF from 'jspdf'

export default {
  name: 'IMCClinico',
  components: { Layout, Footer },

  data() {
    return {
      edad: null,
      sexo: '',
      peso: null,
      talla: null,
      imc: null,
      clasificacion: '',
      fechaCalculo: ''
    }
  },

  computed: {
    esAdulto() {
      return this.edad >= 18
    },

    pesoIdeal() {
      if (!this.talla) return null
      const tallaM = this.talla / 100
      return (22 * tallaM * tallaM).toFixed(1)
    },

    superficieCorporal() {
      if (!this.peso || !this.talla) return null
      return Math.sqrt((this.peso * this.talla) / 3600).toFixed(2)
    },

    badgeClase() {
      if (this.imc < 18.5) return 'bg-info'
      if (this.imc < 25) return 'bg-success'
      if (this.imc < 30) return 'bg-warning'
      return 'bg-danger'
    },

    recomendacion() {
      if (!this.imc) return ''

      if (this.esAdulto) {
        if (this.imc < 18.5) return 'Evaluar estado nutricional y posibles deficiencias.'
        if (this.imc < 25) return 'Mantener hábitos saludables.'
        if (this.imc < 30) return 'Recomendar actividad física y control nutricional.'
        return 'Evaluación clínica y control metabólico recomendado.'
      }

      return 'Resultado orientativo. En pediatría se recomienda evaluar percentiles OMS/CDC.'
    }
  },

  mounted() {
    this.$refs.layoutRef.toggleProcesos()
    this.$refs.layoutRef.toggleReportes()
    this.$refs.layoutRef.toggleParametros()
    this.$refs.layoutRef.toggleAyuda()
  },

  methods: {
    validar() {
      if (!this.edad || !this.sexo || !this.peso || !this.talla) {
        Swal.fire({
          icon: 'warning',
          title: 'Datos incompletos',
          text: 'Complete todos los campos requeridos.',
          confirmButtonText: 'Aceptar'
        })
        return false
      }
      return true
    },

    calcular() {
      if (!this.validar()) return

      const tallaM = this.talla / 100
      const valor = this.peso / (tallaM * tallaM)

      this.imc = Number(valor.toFixed(2))
      this.fechaCalculo = new Date().toLocaleString()

      this.clasificar(valor)
    },

    clasificar(valor) {
      if (this.esAdulto) {
        if (valor < 18.5) this.clasificacion = 'Bajo peso'
        else if (valor < 25) this.clasificacion = 'Peso normal'
        else if (valor < 30) this.clasificacion = 'Sobrepeso'
        else this.clasificacion = 'Obesidad'
      } else {
        if (valor < 14) this.clasificacion = 'Bajo peso (orientativo)'
        else if (valor < 20) this.clasificacion = 'Peso adecuado (orientativo)'
        else if (valor < 24) this.clasificacion = 'Riesgo de sobrepeso'
        else this.clasificacion = 'Sobrepeso'
      }
    },
	
exportarPDF() {
  if (!this.imc) return

  const doc = new jsPDF()

  // Título
  doc.setFontSize(16)
  doc.text('Evaluación Antropométrica (IMC)', 20, 20)

  doc.setFontSize(11)
  doc.text(`Fecha: ${this.fechaCalculo}`, 20, 30)

  // Línea
  doc.line(20, 34, 190, 34)

  // Datos del paciente
  doc.text(`Edad: ${this.edad} años`, 20, 45)
  doc.text(`Sexo: ${this.sexo === 'M' ? 'Masculino' : 'Femenino'}`, 20, 52)

  // Resultados
  doc.text(`Peso: ${this.peso} kg`, 20, 65)
  doc.text(`Talla: ${this.talla} cm`, 20, 72)
  doc.text(`IMC: ${this.imc}`, 20, 79)
  doc.text(`Clasificación: ${this.clasificacion}`, 20, 86)

  doc.text(`Peso ideal estimado: ${this.pesoIdeal} kg`, 20, 98)
  doc.text(`Superficie corporal (BSA): ${this.superficieCorporal} m²`, 20, 105)

  // Recomendación
  doc.setFontSize(10)
  doc.text(
    `Recomendación:\n${this.recomendacion}`,
    20,
    118,
    { maxWidth: 170 }
  )

  // Nota legal
  doc.setFontSize(9)
  doc.text(
    'Este resultado es orientativo y no reemplaza la evaluación médica.',
    20,
    150
  )

  // Guardar
  doc.save(`IMC_${this.fechaCalculo.replace(/[/:]/g, '-')}.pdf`)
}

  }
}
</script>
